/* eslint-disable indent */
import React, { createContext, useContext, useState } from "react";
import { OperationStatus, CDNFileData, CDNFileDetail, CDNFileListItem, defaultCDNFileData } from "./constants";
import { isFileObject } from "../../utils/validation";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { CDNFileService } from "../../services/cdnFileService";
import { useMemo, useCallback } from "react";
import { getErrorMessage } from "../../utils/errorUtils";


interface CDNFileContextState {
	// Core Domain Data
	cdnFileData: CDNFileData;
	cdnFileDetail: CDNFileDetail | null;
    cdnFileList: CDNFileListItem[];
	operationStatus: OperationStatus;

	// Setters
	setCDNFileData: React.Dispatch<React.SetStateAction<CDNFileData>>;
	clearCDNFileData: () => void;
	clearCDNFileList: () => void;

	// Actions
	addCDNFile: () => Promise<void>;
    getCDNFileList: () => Promise<void>;
	updateCDNFile: () => Promise<void>;
	getCDNFileDetails: (id: string) => Promise<void>;
	removeCDNFile: (id: string) => Promise<void>;
}

interface CDNFileProviderProps {
	children: React.ReactNode;
}

const CDNFileContext = createContext<CDNFileContextState | undefined>(
	undefined
);

export const useCDNFileContext = () => {
	const context = useContext(CDNFileContext);
	if (!context) {
		throw new Error(
			"useCDNFileContext must be used within an CDNFileContextProvider"
		);
	}
	return context;
};

export const CDNFileContextProvider: React.FC<CDNFileProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [cdnFileList, setCDNFileList] = useState<CDNFileListItem[]>([]);
	const [cdnFileDetail, setCDNFileDetail] = useState<CDNFileDetail | null>(null);
	const [cdnFileData, setCDNFileData] = useState<CDNFileData>(defaultCDNFileData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const clearCDNFileData = () => {
		setCDNFileData(defaultCDNFileData);
		setCDNFileDetail(null);
	};

	const clearCDNFileList = () => {
		setCDNFileList([]);
	};

	const isCDNFileDataValid = (data: CDNFileData) => {
		if (!cdnFileData.name?.trim() || !isFileObject(data.image)) {
			return false;
		}

		return true;
	};

	const addCDNFile = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isCDNFileDataValid(cdnFileData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await CDNFileService.addCDNFile(cdnFileData);

			if (response.isSuccessful) {
				message.success("CDN Dosyası başarıyla eklendi.");
				setCDNFileData(defaultCDNFileData);
				navigate("/cdn");
			} else {
				message.error(response.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}	
	}, [cdnFileData, navigate]);

	const updateCDNFile = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!cdnFileData.cdnFileId) {
			message.info("CDN Dosya Numarası bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const isNameChanged = cdnFileData.name.trim() && cdnFileData.name !== cdnFileDetail?.name;
		const isImageChanged = cdnFileData.image && isFileObject(cdnFileData.image);

		if (!isNameChanged && !isImageChanged) {
			message.info("Bir değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await CDNFileService.updateCDNFile(cdnFileData);

			if (response.isSuccessful) {
				message.success("CDN Dosyası başarıyla güncellendi.");
				clearCDNFileData();
				navigate("/cdn");
			} else {
				message.error(response.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [cdnFileData, cdnFileDetail, clearCDNFileData, navigate]);

	const getCDNFileDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await CDNFileService.getCDNFileDetail(id);

			if (response.isSuccessful) {
				setCDNFileDetail({
					cdnFileId: response.result.cdnFileId,
					name: response.result.name,
                    imageUrl: response.result.imageUrl
				});

				setCDNFileData(prev => ({
					...prev,
					cdnFileId: response.result.cdnFileId
				}));
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);


	const getCDNFileList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await CDNFileService.getCDNFileList();

			if (response.isSuccessful) {
				setCDNFileList(response.result?.items || []);
				clearCDNFileData();
			} else {
				message.error(response.error ? response.error : "CDN dosyası listesi yüklenirken hata oluştu.");
				setCDNFileList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "CDN dosyası listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [clearCDNFileData]);


	const removeCDNFile = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await CDNFileService.removeCDNFile(id);
			if (response.isSuccessful) {
				message.success("CDN Dosyası başarıyla silindi.");
				await getCDNFileList();
			} else {
				message.error(response.error?.message || "Belge kategorisi silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [getCDNFileList]);

	const contextValue = useMemo<CDNFileContextState>(
		() => ({
			cdnFileData,
			cdnFileList,
			cdnFileDetail,
			setCDNFileData,
			operationStatus,
			addCDNFile,
			removeCDNFile,
			updateCDNFile,
			getCDNFileList,
			getCDNFileDetails,
			clearCDNFileData,
			clearCDNFileList
		}),
		[
			cdnFileData,
			cdnFileList,
			cdnFileDetail,
			setCDNFileData,
			operationStatus,
			addCDNFile,
			removeCDNFile,
			updateCDNFile,
			getCDNFileList,
			getCDNFileDetails,
			clearCDNFileData,
			clearCDNFileList
		]
	);

	return (
		<CDNFileContext.Provider value={contextValue}>
			{children}
		</CDNFileContext.Provider>
	);
};